/**
 * The company's playbook: standing instructions agents follow on its cases.
 * Firm admins add, edit, turn off and delete rules and decide the agent's
 * suggestions (the firm's AGENTS.md works the same way); everyone can read
 * them and see what a case's agent is handed.
 */
import {
  Info,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Sparkles,
  Trash2
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FC } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { AgentRobot } from "@/components/brand";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle
} from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Toggle } from "@/components/ui/toggle";
import { cn } from "@/lib/utils";

import { useFirmPermissions } from "@/hooks/useFirmPermissions";
import { playbookActions } from "../actions";
import { PlaybookAPI } from "../api/companiesApi";
import { CaseTypePicker } from "../components/CaseTypePicker";
import { ExtractPlaybookDialog } from "../components/ExtractPlaybookDialog";
import {
  AGENT_LABEL,
  appliesText,
  byCategory,
  CATEGORY_LABEL,
  liveRules,
  ruleLabel,
  suggestedRules
} from "../playbookModel";
import { usePlaybookPreview } from "../queries";
import {
  PLAYBOOK_AGENTS,
  PLAYBOOK_CATEGORIES,
  type Company,
  type CompanyCaseRow,
  type PlaybookAgent,
  type PlaybookCategory,
  type PlaybookRule,
  type PlaybookRuleInput
} from "../types";

type Draft = Required<
  Pick<PlaybookRuleInput, "text" | "category" | "applies_to" | "agents">
> & { source: string };

const draftOf = (rule: Partial<PlaybookRule>): Draft => ({
  text: rule.text ?? "",
  category: rule.category ?? "filing",
  applies_to: rule.applies_to ?? [],
  agents: rule.agents?.length ? rule.agents : ["case_agent"],
  source: rule.source ?? ""
});

const RuleForm: FC<{
  initial: Draft;
  common: string[];
  submitLabel: string;
  note?: string;
  onSubmit: (draft: Draft) => Promise<unknown>;
  onCancel: () => void;
}> = ({ initial, common, submitLabel, note, onSubmit, onCancel }) => {
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));
  const toggleAgent = (agent: PlaybookAgent, on: boolean) =>
    set(
      "agents",
      on ? [...draft.agents, agent] : draft.agents.filter((a) => a !== agent)
    );
  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit({ ...draft, text: draft.text.trim() });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex flex-col gap-2.5 rounded-lg border border-line-2 bg-fill-1 p-3"
      data-testid="playbook-rule-form"
    >
      {note ? (
        <span className="flex items-center gap-1.5 text-sm text-fg-3">
          <Sparkles className="size-3.5 text-primary" />
          {note}
        </span>
      ) : null}
      <Textarea
        aria-label="Rule"
        value={draft.text}
        className="min-h-16 bg-surface text-base"
        onChange={(e) => set("text", e.currentTarget.value)}
      />
      <div className="grid grid-cols-[88px_minmax(0,1fr)] items-center gap-x-3 gap-y-2 text-base">
        <span className="text-fg-3">Category</span>
        <Select
          value={draft.category}
          onValueChange={(v) => set("category", v as PlaybookCategory)}
        >
          <SelectTrigger
            size="sm"
            aria-label="Category"
            className="h-7 w-44 bg-surface text-sm"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PLAYBOOK_CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>
                {CATEGORY_LABEL[c]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-fg-3">Applies to</span>
        <CaseTypePicker
          value={draft.applies_to}
          common={common}
          onChange={(v) => set("applies_to", v)}
        />
        <span className="text-fg-3">Agents</span>
        <span className="flex flex-wrap gap-1.5">
          {PLAYBOOK_AGENTS.map((a) => (
            <Toggle
              key={a}
              size="sm"
              variant="outline"
              className="h-7 gap-1 bg-surface px-2 text-sm font-normal text-fg-3 data-[state=on]:border-primary/40 data-[state=on]:bg-primary/10 data-[state=on]:font-medium data-[state=on]:text-primary"
              pressed={draft.agents.includes(a)}
              onPressedChange={(on) => toggleAgent(a, on)}
            >
              <AgentRobot state="static" className="size-3" />
              {AGENT_LABEL[a]}
            </Toggle>
          ))}
        </span>
        <Label htmlFor="playbook-rule-source" className="font-normal text-fg-3">
          Source
        </Label>
        <Input
          id="playbook-rule-source"
          value={draft.source}
          placeholder="Where it came from, e.g. Rachel Kim, Sep 12"
          className="h-7 bg-surface text-sm"
          onChange={(e) => set("source", e.currentTarget.value)}
        />
      </div>
      <span className="flex justify-end gap-1.5">
        <Button size="xs" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="xs"
          disabled={busy || !draft.text.trim() || !draft.agents.length}
          onClick={() => void submit()}
        >
          {busy ? <Loader2 className="animate-spin" /> : null}
          {submitLabel}
        </Button>
      </span>
    </div>
  );
};

const Tags: FC<{ rule: PlaybookRule }> = ({ rule }) => (
  <span className="flex flex-wrap items-center gap-1.5">
    <Badge variant="secondary" className="font-normal">
      {appliesText(rule)}
    </Badge>
    {rule.agents.map((a) => (
      <Badge
        key={a}
        variant="secondary"
        className="gap-1 font-normal text-primary"
      >
        <AgentRobot state="static" className="size-3" />
        {AGENT_LABEL[a]}
      </Badge>
    ))}
    {rule.source ? (
      <span className="text-sm text-fg-3">{rule.source}</span>
    ) : null}
  </span>
);

const RuleRow: FC<{
  company: Company;
  rule: PlaybookRule;
  isAdmin: boolean;
  focused: boolean;
  onEdit: () => void;
}> = ({ company, rule, isAdmin, focused, onEdit }) => {
  const ref = useRef<HTMLDivElement>(null);
  const label = ruleLabel(rule) ?? "";
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: "center" });
  }, [focused]);
  return (
    <div
      ref={ref}
      data-testid="playbook-rule"
      data-rule-number={rule.number}
      className={cn(
        "flex gap-3 border-b border-line-2 px-3.5 py-3 last:border-b-0",
        !rule.enabled && "opacity-55",
        focused && "bg-primary/5 ring-1 ring-primary/40 ring-inset"
      )}
    >
      <Switch
        className="mt-0.5"
        aria-label={`${rule.enabled ? "Turn off" : "Turn on"} ${label}`}
        checked={rule.enabled}
        disabled={!isAdmin}
        onCheckedChange={(on) =>
          void playbookActions.update(
            company,
            rule,
            { enabled: on },
            on ? `Turned ${label} on` : `Turned ${label} off for every case`
          )
        }
      />
      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="text-base leading-4.75">
          <span className="mr-1.5 font-mono text-sm text-fg-3">{label}</span>
          {rule.text}
        </span>
        <Tags rule={rule} />
      </span>
      {isAdmin ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon-xs"
              variant="ghost"
              aria-label={`More for ${label}`}
            >
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onEdit}>
              <Pencil /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              variant="destructive"
              onSelect={() => void playbookActions.remove(company, rule)}
            >
              <Trash2 /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
};

const Suggestion: FC<{
  company: Company;
  rule: PlaybookRule;
  isAdmin: boolean;
  common: string[];
}> = ({ company, rule, isAdmin, common }) => {
  const [editing, setEditing] = useState(false);
  const accept = (edits: PlaybookRuleInput = {}) =>
    playbookActions.decide(company, rule, "accept", edits);
  if (editing) {
    return (
      <RuleForm
        initial={draftOf(rule)}
        common={common}
        submitLabel="Add rule"
        onSubmit={(d) => accept(d)}
        onCancel={() => setEditing(false)}
      />
    );
  }
  return (
    <div
      className="flex gap-2.5 rounded-lg border border-primary/25 bg-primary/5 px-3.5 py-3"
      data-testid="playbook-suggestion"
    >
      <Sparkles className="mt-0.5 size-4 shrink-0 text-primary" />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-sm font-medium text-primary">
          Suggested by Casey · {CATEGORY_LABEL[rule.category]}
        </span>
        <span className="text-base font-medium">{rule.text}</span>
        <span className="text-sm text-fg-3">
          {[rule.reason, rule.source, `Would apply to ${appliesText(rule)}.`]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </span>
      {isAdmin ? (
        <span className="flex items-start gap-1.5">
          <Button
            size="xs"
            variant="ghost"
            onClick={() =>
              void playbookActions.decide(company, rule, "dismiss")
            }
          >
            Dismiss
          </Button>
          <Button
            size="xs"
            variant="secondary"
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
          <Button variant="primary" size="xs" onClick={() => void accept()}>
            <Plus /> Add rule
          </Button>
        </span>
      ) : (
        <span className="text-sm text-fg-3">Waiting for a firm admin</span>
      )}
    </div>
  );
};

const AddRule: FC<{ company: Company; common: string[] }> = ({
  company,
  common
}) => {
  const [text, setText] = useState("");
  // Rules typed while one is being filed wait their turn rather than replace it.
  const [queue, setQueue] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<{ draft: Draft; note: string } | null>(
    null
  );
  useEffect(() => {
    if (busy || draft || !queue.length) return;
    const [next] = queue;
    setBusy(true);
    PlaybookAPI.categorize(company.id, next)
      .then((out) =>
        setDraft({
          draft: draftOf(out),
          note: out.guessed
            ? "Filed by keywords. Check the category and case types."
            : `Filed under ${
                CATEGORY_LABEL[out.category]
              }. Check it and the case types.`
        })
      )
      .catch((error: unknown) => {
        toast.error(
          error instanceof Error ? error.message : "Couldn't read the rule"
        );
        setText((typed) => typed || next);
      })
      .finally(() => {
        setQueue((q) => q.slice(1));
        setBusy(false);
      });
  }, [busy, draft, queue, company.id]);
  const waiting = queue.length - (busy ? 1 : 0);
  const queued =
    waiting > 0
      ? `${waiting} more rule${waiting === 1 ? "" : "s"} queued`
      : null;

  if (draft) {
    return (
      <div className="flex flex-col gap-1.5">
        <RuleForm
          initial={draft.draft}
          note={draft.note}
          common={common}
          submitLabel="Add rule"
          onSubmit={async (d) => {
            const out = await playbookActions.add(company, d);
            if (out) setDraft(null);
          }}
          onCancel={() => setDraft(null)}
        />
        {queued ? (
          <span className="text-sm text-fg-3" data-testid="rules-queued">
            {queued}
          </span>
        ) : null}
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2">
      <form
        className="flex min-w-0 flex-1 gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const rule = text.trim();
          if (!rule) return;
          setQueue((q) => [...q, rule]);
          setText("");
        }}
      >
        <Input
          aria-label="New rule"
          value={text}
          placeholder="Add a rule in plain English, e.g. Never file on Fridays"
          className="h-8 max-w-110 text-base"
          onChange={(e) => setText(e.currentTarget.value)}
        />
        <Button
          type="submit"
          size="sm"
          variant="secondary"
          disabled={!text.trim()}
        >
          {busy ? <Loader2 className="animate-spin" /> : <Plus />} Add
        </Button>
        {queued ? (
          <span
            className="self-center text-sm text-fg-3"
            data-testid="rules-queued"
          >
            {queued}
          </span>
        ) : null}
      </form>
      <ExtractPlaybookDialog company={company} />
    </div>
  );
};

const AgentView: FC<{ company: Company; cases: CompanyCaseRow[] }> = ({
  company,
  cases
}) => {
  const options = useMemo(() => {
    const linked = cases.filter((c) => c.linked);
    const open = linked.filter((c) => c.open);
    return open.length ? open : linked;
  }, [cases]);
  const [pick, setPick] = useState<string | undefined>(undefined);
  const selected =
    options.find((c) => c.individual_id === pick) ?? options[0] ?? null;
  const preview = usePlaybookPreview(company.id, selected?.individual_id);
  const on = preview.data?.rules.filter((r) => r.on).length ?? 0;
  const off = (preview.data?.rules.length ?? 0) - on;

  return (
    <aside
      className="hidden w-95 shrink-0 flex-col border-l border-line-2 bg-fill-1 lg:flex"
      data-testid="playbook-agent-view"
    >
      <div className="flex flex-col gap-2.5 border-b border-line-2 px-4 py-3.5">
        <span className="text-base font-medium">What Casey Sees</span>
        {options.length ? (
          <Select
            value={selected?.individual_id}
            onValueChange={(v) => setPick(v)}
          >
            <SelectTrigger
              size="sm"
              aria-label="Case"
              className="h-7 w-full bg-surface text-sm"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map((c) => (
                <SelectItem key={c.individual_id} value={c.individual_id}>
                  {c.visa ? `${c.name} · ${c.visa}` : c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
        {!selected ? (
          <span className="text-sm text-fg-3">
            Link a case to {company.name} to see what its agent reads.
          </span>
        ) : preview.isLoading || !preview.data ? (
          <Skeleton className="h-40" />
        ) : (
          <>
            <span className="text-sm text-fg-3">
              {preview.data.live_total ? (
                <>
                  {on} of {preview.data.live_total}{" "}
                  {preview.data.live_total === 1
                    ? "rule applies"
                    : "rules apply"}{" "}
                  to {selected.name}&apos;s {selected.visa ?? "case"}
                  {off ? `, ${off} turned off on the case` : ""}.
                </>
              ) : (
                `No rules yet, so the agent gets ${company.name}'s details only.`
              )}
            </span>
            <pre
              className="rounded-lg border border-line-2 bg-surface px-3 py-2.5 font-mono text-sm leading-4.5 whitespace-pre-wrap"
              data-testid="playbook-preview"
            >
              {preview.data.text}
            </pre>
            <span className="flex gap-2 text-sm leading-4.25 text-fg-3">
              <Info className="mt-0.5 size-3.5 shrink-0" />
              Loaded as {preview.data.path} next to the firm&apos;s AGENTS.md
              and the client&apos;s NOTES.md. Rules for other case types stay
              out. Agents cite a rule by its label when they act on it, so you
              can trace an action back to {company.name}&apos;s instruction.
            </span>
          </>
        )}
      </div>
    </aside>
  );
};

type Props = { company: Company; cases: CompanyCaseRow[] };

export const PlaybookTab: FC<Props> = ({ company, cases }) => {
  const isAdmin = useFirmPermissions().can("edit_company_playbook");
  const [params] = useSearchParams();
  const focus = Number(params.get("rule") ?? 0);
  const [editing, setEditing] = useState<string | null>(null);
  const live = liveRules(company.playbook ?? []);
  const suggested = suggestedRules(company.playbook ?? []);
  const groups = byCategory(live);
  const common = useMemo(
    () =>
      Array.from(
        new Set(cases.map((c) => c.visa).filter((v): v is string => !!v))
      ),
    [cases]
  );

  return (
    <div className="flex min-h-0 flex-1" data-testid="company-playbook">
      <div className="flex min-w-0 flex-1 flex-col gap-3.5 overflow-y-auto px-5 py-4">
        <p className="max-w-180 text-base leading-4.75 text-fg-3">
          {company.name}&apos;s standing instructions. Agents read the rules
          that match a case&apos;s type on every run, cite the rule when they
          follow it, and ask before doing something a rule forbids. A case can
          turn a rule off for itself.
          {isAdmin
            ? ""
            : " Firm admins change the playbook; you can turn a rule off for one case from the case's company panel."}
        </p>
        {suggested.map((r) => (
          <Suggestion
            key={r.id}
            company={company}
            rule={r}
            isAdmin={isAdmin}
            common={common}
          />
        ))}
        {isAdmin ? <AddRule company={company} common={common} /> : null}
        {groups.length ? (
          groups.map((g) => (
            <div key={g.category} className="flex flex-col gap-1.5">
              <span className="flex items-center gap-1.5 text-sm font-medium text-fg-3">
                {CATEGORY_LABEL[g.category]}
                <span className="tabular-nums">{g.rules.length}</span>
              </span>
              <div className="shrink-0 overflow-hidden rounded-lg border border-line-2">
                {g.rules.map((r) =>
                  editing === r.id ? (
                    <div key={r.id} className="p-2">
                      <RuleForm
                        initial={draftOf(r)}
                        common={common}
                        submitLabel="Save"
                        onSubmit={async (d) => {
                          const out = await playbookActions.update(
                            company,
                            r,
                            d,
                            `Saved ${ruleLabel(r) ?? "the rule"}`
                          );
                          if (out) setEditing(null);
                        }}
                        onCancel={() => setEditing(null)}
                      />
                    </div>
                  ) : (
                    <RuleRow
                      key={r.id}
                      company={company}
                      rule={r}
                      isAdmin={isAdmin}
                      focused={focus === r.number}
                      onEdit={() => setEditing(r.id)}
                    />
                  )
                )}
              </div>
            </div>
          ))
        ) : (
          <Empty className="border border-dashed border-line-2">
            <EmptyHeader>
              <EmptyTitle>No rules yet</EmptyTitle>
              <EmptyDescription>
                {isAdmin ? (
                  <>
                    Add {company.name}&apos;s instructions in plain English,
                    like &ldquo;Copy HR on every RFE&rdquo;. Casey also suggests
                    rules from the company&apos;s emails.
                  </>
                ) : (
                  <>Ask an admin to add {company.name}&apos;s instructions.</>
                )}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}
      </div>
      <AgentView company={company} cases={cases} />
    </div>
  );
};
